/**
 * Transport for the realtime endpoints of the end-user API (Phases 1–5):
 *
 *   GET  /v1/api/me/realtime/bootstrap      → {@link fetchBootstrap}
 *   POST /v1/api/me/realtime/auth           → {@link authorizeChannel}
 *   GET  /v1/api/me/notifications/changes   → {@link fetchChanges}
 *   POST /v1/api/me/realtime/receipts       → {@link postReceipts}
 *   POST /v1/api/me/realtime/test           → {@link sendTest}
 *   PUT  /v1/api/me/notification-endpoints/{installation_id}    → {@link registerEndpoint}
 *   DELETE /v1/api/me/notification-endpoints/{installation_id} → {@link revokeEndpoint}
 *
 * Bootstrap, changes and receipts go through a small `fetch` helper rather
 * than the SDK: `ErpatClient.request()` takes no custom headers, and these
 * three are stamped with `X-Installation-ID` — the persistent device id — so
 * the server can attribute a cursor drain and a delivery receipt to ONE
 * installation (the receipts endpoint refuses the call without it). The
 * helper mirrors what the SDK would do: the same bearer + `X-Company-Key`,
 * the same `{ok, data}` / `{ok:false, error}` envelope, and the same one-shot
 * 401 refresh-and-retry through the single-flight `refreshSession()`, with a
 * refresh ERPat refuses tearing the session down exactly as `withAuthRetry`
 * does — and one that merely did not get through leaving it alone.
 *
 * Channel authorization is different again: pusher-js's contract is an
 * `application/x-www-form-urlencoded` POST that answers with the bare
 * `{"auth": "..."}` object — no envelope — so it has its own sender.
 *
 * Nothing here logs or rethrows server text: errors surface as a short
 * content-free code (plus the HTTP status, which the service branches on) so
 * the realtime store can display "something is wrong" without ever echoing a
 * channel name, token or response body.
 */
import { erpatClient, getSessionEpoch, getStoredCompanyKey } from '@/services/api/erpat-client';
import {
  asRecord,
  domainBody,
  expireAfterFailedRefresh,
  isSessionCancelled,
  pick,
  refreshSession,
  unwrap,
} from '@/services/api/http';
import { API_BASE_URL, APP_VERSION } from '@/utils/env';
import { getDeviceId } from '@/utils/device';
import { SUPPORTED_EVENT_VERSIONS } from '@/contracts/realtime-envelope-v1';
import {
  ACTIVITY_TIMEOUT_SECONDS_DEFAULT,
  CHANGES_PAGE_SIZE,
  POLL_FALLBACK_SECONDS,
} from '@/contracts/realtime-policy';

const BOOTSTRAP_PATH = '/v1/api/me/realtime/bootstrap';
const AUTH_PATH = '/v1/api/me/realtime/auth';
const TEST_PATH = '/v1/api/me/realtime/test';
const CHANGES_PATH = '/v1/api/me/notifications/changes';
const RECEIPTS_PATH = '/v1/api/me/realtime/receipts';
const ENDPOINTS_PATH = '/v1/api/me/notification-endpoints';

/** Shape of a valid Pusher channel-authorization answer: `<app_key>:<64 hex>`. */
const AUTH_TOKEN_RE = /^[^:\s]+:[0-9a-f]{64}$/;

/** The server accepts only a UUID as an installation id (anything else is 422). */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** pusher-js connection settings the server hands us (never guessed client-side). */
export interface RealtimeBootstrapPusher {
  appKey: string;
  wsHost: string;
  wsPort: number;
  wssPort: number;
  forceTls: boolean;
  /** The gateway's WebSocket path (`/app`); informational — see the service. */
  wsPath: string;
  activityTimeoutSeconds: number;
}

/** Unread totals the server sends with a snapshot and with every changes page. */
export interface RealtimeUnreadCounts {
  notifications: number;
  messages: number;
}

/**
 * The bootstrap snapshot: the newest notifications (raw rows — the inbox page
 * fetches its own list through the REST mapper, so these are not rendered),
 * the unread totals, and the stream position they are current as of.
 */
export interface RealtimeSnapshot {
  notifications: unknown[];
  unreadCounts: RealtimeUnreadCounts;
  asOfSequence: string;
}

export interface RealtimeBootstrap {
  schemaVersion: number;
  supportedEventVersions: number[];
  eventName: string;
  provider: string;
  enabled: boolean;
  /** `null` when realtime is disabled for this tenant. */
  pusher: RealtimeBootstrapPusher | null;
  authEndpoint: string;
  testEndpoint: string;
  changesEndpoint: string;
  receiptsEndpoint: string;
  endpointsEndpoint: string;
  /** The ONLY source of the channel name; `null` when disabled. */
  personalChannel: string | null;
  tenantScope: string;
  recipientId: string;
  snapshot: RealtimeSnapshot | null;
  /** Opaque signed stream position to drain from; `null` when the stream is off. */
  cursor: string | null;
  hostNotifications: {
    policy: string;
    provider: string | null;
    webPushAvailable: boolean;
    vapidPublicKey: string | null;
    /** Fraction of `client_received` receipts to send (0..1). */
    receiptSampleRate: number;
  };
  features: {
    realtime: boolean;
    hostPush: boolean;
    receipts: boolean;
  };
  pollIntervalSeconds: number;
}

/** One page of `GET /me/notifications/changes`. */
export interface RealtimeChangesPage {
  /** Stored envelopes (each also carries `tenant_scope`, `sequence`, `cursor`). */
  events: unknown[];
  nextCursor: string | null;
  hasMore: boolean;
  unreadCounts: RealtimeUnreadCounts | null;
  countsAsOfSequence: string;
}

/** One delivery receipt, as the service queues it. */
export interface RealtimeReceipt {
  eventId: string;
  type: 'client_received' | 'opened';
  /** RFC 3339 UTC instant with a `Z` suffix, second precision. */
  observedAt: string;
}

/* ── Endpoint registration (phase6-contract.md §3.2.4, mirrors RealtimeEndpointService) ── */

export type EndpointProvider = 'web_push' | 'fcm' | 'apns';
/** The server's exact enum: the web platform is qualified by OS (RealtimeEndpointService::PLATFORMS). */
export type EndpointPlatform = 'web_windows' | 'web_macos' | 'web_linux' | 'web_android' | 'web_ios' | 'android' | 'ios';
export type EndpointEnvironment = 'production' | 'sandbox';

/** A Web Push subscription as `PushSubscription.toJSON()` shapes it (base64url keys). */
export interface WebPushCredential {
  endpoint: string;
  /** RFC 3339 UTC second, or null — a numeric `expirationTime` is converted by the caller. */
  expiration_time: string | null;
  keys: { p256dh: string; auth: string };
}

/** A native push token (FCM / APNs): ≤ 4096 printable ASCII characters. */
export interface NativeTokenCredential {
  token: string;
}

export interface EndpointRegistrationBody {
  provider: EndpointProvider;
  platform: EndpointPlatform;
  environment: EndpointEnvironment;
  /** REQUIRED for `fcm` / `apns` (the server allow-lists it); must be ABSENT for `web_push`. */
  application_id?: string;
  /** BCP-47 tag, e.g. `en-PH`. */
  locale: string;
  /** IANA zone, e.g. `Asia/Manila`. */
  timezone: string;
  credential: WebPushCredential | NativeTokenCredential;
}

/**
 * What a PUT answers: 201 on a first registration (or a cross-user rebind), 200
 * on a refresh. `activationStreamId` is the stream position the endpoint was
 * activated at (nothing older is ever pushed to it); `activationCursor` is the
 * signed cursor for that position — opaque, kept only as the local
 * registration record's handle.
 */
export interface EndpointRegistration {
  status: 201 | 200;
  provider: EndpointProvider | null;
  activationStreamId: string | null;
  activationCursor: string | null;
}

/** A content-free failure from the realtime transport. */
export class RealtimeApiError extends Error {
  readonly code: string;
  /**
   * HTTP status, or `0` for a transport-level failure (network, abort, no
   * session, a token refresh that did not get through — `refresh_failed`).
   */
  readonly status: number;

  constructor(code: string, status = 0) {
    super(code);
    this.name = 'RealtimeApiError';
    this.code = code;
    this.status = status;
  }
}

/* ── Mapping helpers (same defensive style as services/api/models.ts) ─────── */

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : fallback;
}

function num(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

/** A TCP port pusher-js can actually write into the socket URL. */
function isPort(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= 65_535;
}

function bool(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  return fallback;
}

function nullableStr(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function mapPusher(value: unknown): RealtimeBootstrapPusher | null {
  if (value === null || value === undefined) return null;
  const rec = asRecord(value);
  const appKey = str(rec.app_key);
  const wsHost = str(rec.ws_host);
  // Without a key and a host there is nothing to connect to; treat as disabled
  // rather than letting pusher-js dial `undefined`.
  if (!appKey || !wsHost) return null;
  // pusher-js writes the port into the socket URL on every surface, so a
  // garbled `ws_port` must NOT default to 80 — `wss://host:80` would be dialled
  // from the PWA, Android and iOS alike. ERPat always sends all seven keys
  // (`wss_port` = `ws_port`); this only guards a malformed server, which then
  // reads as `disabled` like any other unusable block. A garbled `wss_port`
  // falls back to `ws_port`, the value the server derives it from.
  const wsPort = num(rec.ws_port, Number.NaN);
  if (!isPort(wsPort)) return null;
  const wssPort = num(rec.wss_port, wsPort);
  return {
    appKey,
    wsHost,
    wsPort,
    wssPort: isPort(wssPort) ? wssPort : wsPort,
    forceTls: bool(rec.force_tls),
    wsPath: str(rec.ws_path, '/app'),
    activityTimeoutSeconds: num(rec.activity_timeout_seconds, ACTIVITY_TIMEOUT_SECONDS_DEFAULT),
  };
}

function mapVersions(value: unknown): number[] {
  const list = Array.isArray(value) ? value : [];
  const versions = list.map((v) => num(v, Number.NaN)).filter((v) => Number.isInteger(v));
  // A server that omits the list is a Phase 1 server: exactly what we support.
  return versions.length > 0 ? versions : [...SUPPORTED_EVENT_VERSIONS];
}

function mapUnreadCounts(value: unknown): RealtimeUnreadCounts | null {
  if (value === null || value === undefined) return null;
  const rec = asRecord(value);
  return {
    notifications: Math.max(0, Math.trunc(num(rec.notifications, 0))),
    messages: Math.max(0, Math.trunc(num(rec.messages, 0))),
  };
}

function mapSnapshot(value: unknown): RealtimeSnapshot | null {
  if (value === null || value === undefined) return null;
  const rec = asRecord(value);
  return {
    notifications: Array.isArray(rec.notifications) ? rec.notifications : [],
    unreadCounts: mapUnreadCounts(rec.unread_counts) ?? { notifications: 0, messages: 0 },
    asOfSequence: str(rec.as_of_sequence, '0'),
  };
}

/** Map the raw bootstrap `data` into the typed shape the service consumes. */
export function mapRealtimeBootstrap(data: unknown): RealtimeBootstrap {
  const rec = domainBody(data, ['bootstrap', 'realtime']);
  const host = asRecord(rec.host_notifications);
  const features = asRecord(rec.features);
  const pusher = mapPusher(rec.pusher);
  const personalChannel = nullableStr(rec.personal_channel);
  const enabled = bool(rec.enabled) && pusher !== null && personalChannel !== null;
  const sampleRate = num(host.receipt_sample_rate, 0);
  return {
    schemaVersion: num(rec.schema_version, 1),
    supportedEventVersions: mapVersions(rec.supported_event_versions),
    eventName: str(rec.event_name),
    provider: str(rec.provider, 'disabled'),
    enabled,
    pusher: enabled ? pusher : null,
    authEndpoint: str(rec.auth_endpoint, AUTH_PATH),
    testEndpoint: str(rec.test_endpoint, TEST_PATH),
    changesEndpoint: str(rec.changes_endpoint, CHANGES_PATH),
    receiptsEndpoint: str(rec.receipts_endpoint, RECEIPTS_PATH),
    endpointsEndpoint: str(rec.endpoints_endpoint, ENDPOINTS_PATH),
    personalChannel: enabled ? personalChannel : null,
    tenantScope: str(rec.tenant_scope),
    recipientId: str(rec.recipient_id),
    snapshot: mapSnapshot(rec.snapshot),
    cursor: nullableStr(rec.cursor),
    hostNotifications: {
      policy: str(host.policy, 'disabled'),
      provider: nullableStr(host.provider),
      webPushAvailable: bool(host.web_push_available),
      vapidPublicKey: nullableStr(host.vapid_public_key),
      receiptSampleRate: Math.min(1, Math.max(0, sampleRate)),
    },
    features: {
      realtime: bool(features.realtime) && enabled,
      hostPush: bool(features.host_push),
      receipts: bool(features.receipts),
    },
    pollIntervalSeconds: Math.max(5, num(rec.poll_interval_seconds, POLL_FALLBACK_SECONDS)),
  };
}

function mapChangesPage(data: unknown): RealtimeChangesPage {
  const rec = domainBody(data, ['changes', 'stream']);
  return {
    events: Array.isArray(rec.events) ? rec.events : [],
    nextCursor: nullableStr(rec.next_cursor),
    hasMore: bool(rec.has_more),
    unreadCounts: mapUnreadCounts(rec.unread_counts),
    countsAsOfSequence: str(rec.counts_as_of_sequence, '0'),
  };
}

/* ── Installation identity ────────────────────────────────────────────────── */

/**
 * The persistent device id as the server accepts it (a lower-case UUID), or
 * `null` when this device only has the non-UUID fallback id — in which case
 * the header is omitted and receipts stay off for this installation.
 */
export function installationId(): string | null {
  const id = getDeviceId();
  return UUID_RE.test(id) ? id.toLowerCase() : null;
}

/* ── Session-bound fetch (bearer + company key + installation id) ─────────── */

interface RealtimeFetchInit {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  query?: Readonly<Record<string, string>>;
  body?: unknown;
  signal?: AbortSignal;
  /** `fetch` keepalive: the request outlives the page (a sign-out that unloads). Body-less calls only. */
  keepalive?: boolean;
}

function sessionHeaders(withJsonBody: boolean): Record<string, string> | null {
  const token = erpatClient.getAccessToken();
  if (!token) return null;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };
  if (withJsonBody) headers['Content-Type'] = 'application/json';
  const companyKey = getStoredCompanyKey() ?? erpatClient.getCompanyKey();
  if (companyKey) headers['X-Company-Key'] = companyKey;
  const installation = installationId();
  if (installation) headers['X-Installation-ID'] = installation;
  return headers;
}

function apiUrl(path: string, query?: Readonly<Record<string, string>>): string {
  const base = API_BASE_URL.replace(/\/+$/, '');
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== '') params.set(key, value);
  }
  const qs = params.toString();
  return `${base}${path}${qs ? `?${qs}` : ''}`;
}

async function send(path: string, init: RealtimeFetchInit): Promise<Response> {
  const headers = sessionHeaders(init.body !== undefined);
  if (!headers) throw new RealtimeApiError('no_session');
  try {
    return await fetch(apiUrl(path, init.query), {
      method: init.method,
      headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      signal: init.signal,
      // Never answered from the browser's HTTP cache — same rule, and reason, as
      // `sessionFetch()` in erpat-client.ts, which this bare fetch bypasses.
      cache: 'no-store',
      ...(init.keepalive ? { keepalive: true } : {}),
    });
  } catch (err) {
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    throw new RealtimeApiError(aborted ? 'aborted' : 'network_error');
  }
}

/** Resolve the `{ok, data}` envelope, or throw the server's error CODE (never its message). */
async function readEnvelope(response: Response): Promise<unknown> {
  let envelope: Record<string, unknown> | null = null;
  const text = await response.text();
  if (text.length > 0) {
    try {
      envelope = asRecord(JSON.parse(text));
    } catch {
      throw new RealtimeApiError('invalid_response', response.status);
    }
  }
  if (!response.ok || (envelope !== null && envelope.ok === false)) {
    const error = envelope !== null && envelope.ok === false ? asRecord(envelope.error) : {};
    const code = typeof error.code === 'string' && error.code !== '' ? error.code : `http_${response.status}`;
    throw new RealtimeApiError(code, response.status);
  }
  return envelope !== null && envelope.ok === true ? envelope.data : undefined;
}

/**
 * What a failed token refresh is reported as, once `expireAfterFailedRefresh()`
 * has had its say. That function ends the session only on ERPat's own refusal
 * of the refresh token, so "the refresh failed" no longer implies "the session
 * is over":
 *
 *  - the session ENDED (the teardown bumps the session epoch), or is ending (a
 *    sign-out cancelled the refresh; its teardown has not reached the epoch
 *    yet) → `session_expired`, as before: the service goes quiet, there is
 *    nothing to schedule;
 *  - otherwise the session SURVIVED — the refresh merely did not get through
 *    (no network, a timeout, an edge's security check answering in ERPat's
 *    place). Claiming an expiry there would park the realtime service for good
 *    on a session that is still alive. It is a transport-level failure instead
 *    (`refresh_failed`, status 0): the bootstrap takes its ordinary
 *    retry-with-back-off path, and the drain its status-0 back-off.
 */
function refreshFailure(err: unknown, epoch: number): RealtimeApiError {
  if (getSessionEpoch() !== epoch || isSessionCancelled(err)) {
    return new RealtimeApiError('session_expired', 401);
  }
  return new RealtimeApiError('refresh_failed');
}

/**
 * One session-bound request with the SDK's 401 semantics: refresh once and
 * retry; a refresh ERPat refuses ends the session, one that did not get through
 * is a transport failure ({@link refreshFailure}). The session-epoch guard
 * makes sure a sign-out that races the refresh wins. Returns the raw response
 * so a caller can branch on the status (201 vs 200, 204).
 */
async function realtimeRequest(path: string, init: RealtimeFetchInit): Promise<Response> {
  const epoch = getSessionEpoch();
  let response = await send(path, init);

  if (response.status === 401 && erpatClient.getRefreshToken()) {
    try {
      await refreshSession();
    } catch (err) {
      // Ends the session only on ERPat's own refusal — not when a sign-out
      // cancelled the refresh, and not when the refresh never got an answer.
      expireAfterFailedRefresh(err, epoch);
      throw refreshFailure(err, epoch);
    }
    if (getSessionEpoch() !== epoch) throw new RealtimeApiError('session_changed', 401);
    response = await send(path, init);
  }

  return response;
}

/** {@link realtimeRequest} resolved through the `{ok, data}` envelope. */
async function realtimeFetch(path: string, init: RealtimeFetchInit): Promise<unknown> {
  return readEnvelope(await realtimeRequest(path, init));
}

/* ── Endpoints ─────────────────────────────────────────────────────────────── */

/** `GET /me/realtime/bootstrap` — connection settings, snapshot, cursor, and the caller's personal channel. */
export async function fetchBootstrap(signal?: AbortSignal): Promise<RealtimeBootstrap> {
  const data = await realtimeFetch(BOOTSTRAP_PATH, { method: 'GET', signal });
  return mapRealtimeBootstrap(data);
}

/**
 * `GET /me/notifications/changes?cursor=&limit=` — the stream after `cursor`.
 *
 * Throws a {@link RealtimeApiError} whose `status` the service branches on:
 * 410 `cursor_expired` (re-bootstrap for a fresh snapshot), 422
 * `invalid_cursor` (drop the cursor, then re-bootstrap), 404
 * `module_unavailable` (the notifications module is off — poll instead).
 */
export async function fetchChanges(
  cursor: string | null,
  limit = CHANGES_PAGE_SIZE,
  signal?: AbortSignal,
): Promise<RealtimeChangesPage> {
  const query: Record<string, string> = {
    limit: String(Math.max(1, Math.min(CHANGES_PAGE_SIZE, Math.trunc(limit)))),
  };
  if (cursor) query.cursor = cursor;
  const data = await realtimeFetch(CHANGES_PATH, { method: 'GET', query, signal });
  return mapChangesPage(data);
}

/**
 * `POST /me/realtime/receipts` — up to 100 delivery receipts for this
 * installation. Best-effort by contract: the caller drops a batch that fails.
 */
export async function postReceipts(receipts: readonly RealtimeReceipt[], signal?: AbortSignal): Promise<void> {
  if (receipts.length === 0) return;
  await realtimeFetch(RECEIPTS_PATH, {
    method: 'POST',
    signal,
    body: {
      receipts: receipts.slice(0, 100).map((receipt) => ({
        event_id: receipt.eventId,
        type: receipt.type,
        observed_at: receipt.observedAt,
        client_release: APP_VERSION,
      })),
    },
  });
}

/**
 * `PUT /me/notification-endpoints/{installation_id}` — register (201) or refresh
 * (200) this installation's host-push endpoint. The path id IS the
 * `X-Installation-ID` header (both lower-case); the body echoes it. Throws a
 * {@link RealtimeApiError} the device boundary branches on by `status`:
 * 404 `module_unavailable` (push is not offered — clear the local record and
 * stop asking), 422 `validation_failed` (the credential or, for native, the
 * `application_id` allow-list — mark the installation `invalid`),
 * `no_installation` (status 0) when this device has no UUID id.
 */
export async function registerEndpoint(
  body: EndpointRegistrationBody,
  signal?: AbortSignal,
): Promise<EndpointRegistration> {
  const installation = installationId();
  if (!installation) throw new RealtimeApiError('no_installation');
  const response = await realtimeRequest(`${ENDPOINTS_PATH}/${encodeURIComponent(installation)}`, {
    method: 'PUT',
    signal,
    body: { installation_id: installation, ...body },
  });
  const data = asRecord(await readEnvelope(response));
  const provider = str(data.provider);
  return {
    status: response.status === 201 ? 201 : 200,
    provider: provider === 'web_push' || provider === 'fcm' || provider === 'apns' ? provider : null,
    activationStreamId: nullableStr(data.activation_stream_id),
    activationCursor: nullableStr(data.activation_cursor),
  };
}

/**
 * `DELETE /me/notification-endpoints/{installation_id}` — revoke this
 * installation's endpoint. Idempotent on the server (204 whether or not an
 * active row existed); a 404 (push not offered) resolves too — there is
 * nothing to revoke. Every other failure throws — including
 * `no_installation` (status 0) when this device has no UUID id, exactly like
 * {@link registerEndpoint}: a caller that never registered has no record to
 * revoke and must not mistake a skipped call for a successful one. Called
 * BEFORE sign-out and on an explicit Disable (DEC-090).
 */
export async function revokeEndpoint(signal?: AbortSignal): Promise<void> {
  const installation = installationId();
  if (!installation) throw new RealtimeApiError('no_installation');
  // keepalive: the caller only WAITS a bounded time (DEC-090) — the request
  // itself must still reach the server, even when the page unloads on a
  // sign-out a moment later.
  const response = await realtimeRequest(`${ENDPOINTS_PATH}/${encodeURIComponent(installation)}`, {
    method: 'DELETE',
    signal,
    keepalive: true,
  });
  if (response.status === 204 || response.status === 404) return;
  await readEnvelope(response);
}

/** One form-encoded auth POST with the current bearer + tenant headers. */
function postChannelAuth(socketId: string, channelName: string, signal?: AbortSignal): Promise<Response> {
  const token = erpatClient.getAccessToken();
  if (!token) return Promise.reject(new RealtimeApiError('no_session'));

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded',
    Authorization: `Bearer ${token}`,
  };
  const companyKey = getStoredCompanyKey() ?? erpatClient.getCompanyKey();
  if (companyKey) headers['X-Company-Key'] = companyKey;

  const body = new URLSearchParams({ socket_id: socketId, channel_name: channelName });
  return fetch(apiUrl(AUTH_PATH), { method: 'POST', headers, body, signal });
}

/**
 * `POST /me/realtime/auth` — sign a private-channel subscription for pusher-js.
 *
 * Returns the raw Pusher shape `{ auth }`. A 401 is retried exactly once after
 * a token refresh (the bearer may have expired between socket drops); a
 * refresh ERPat refuses ends the session through the same path every other
 * transport uses, one that did not get through is a transport failure
 * ({@link refreshFailure}), and the session-epoch guard makes sure a sign-out
 * that races the refresh wins.
 */
export async function authorizeChannel(
  socketId: string,
  channelName: string,
  signal?: AbortSignal,
): Promise<{ auth: string }> {
  const epoch = getSessionEpoch();
  let response = await postChannelAuth(socketId, channelName, signal);

  if (response.status === 401 && erpatClient.getRefreshToken()) {
    try {
      await refreshSession();
    } catch (err) {
      // Ends the session only on ERPat's own refusal — not when a sign-out
      // cancelled the refresh, and not when the refresh never got an answer.
      expireAfterFailedRefresh(err, epoch);
      throw refreshFailure(err, epoch);
    }
    // A teardown while we awaited means this socket belongs to a dead session.
    if (getSessionEpoch() !== epoch) throw new RealtimeApiError('session_changed', 401);
    response = await postChannelAuth(socketId, channelName, signal);
  }

  if (!response.ok) throw new RealtimeApiError(`http_${response.status}`, response.status);

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new RealtimeApiError('invalid_auth_response', response.status);
  }
  const auth = asRecord(payload).auth;
  if (typeof auth !== 'string' || !AUTH_TOKEN_RE.test(auth)) {
    throw new RealtimeApiError('invalid_auth_response', response.status);
  }
  return { auth };
}

/** `POST /me/realtime/test` — publish a test envelope to the caller's own channel. */
export async function sendTest(signal?: AbortSignal): Promise<{ eventId: string }> {
  const data = await unwrap(() => erpatClient.request(TEST_PATH, { method: 'POST', signal }));
  const rec = domainBody(data, ['test', 'result']);
  return { eventId: str(pick(rec, ['event_id', 'eventId'], '')) };
}
